import apiClient from '../api/api';
import { API_ENDPOINTS } from '../api/api.config';

export interface DocumentType {
  file_type: string;
  total_count: number;
}

export interface DocumentMetrics {
  document_store_count: number;
  files_count: number;
  processed_today: number;
  processing_queue: number;
  document_type: DocumentType[];
}

export interface ProcessingData {
  name: string;
  overall_processing: {
    start_time: string;
    end_time: string;
    time_taken: number;
    status: string;
    error?: { name: string };
  };
}

export interface DashboardMetricsResponse {
  success: boolean;
  code: number;
  data: DocumentMetrics;
}

export interface DashboardProcessingVolumeResponse {
  success: boolean;
  code: number;
  data: ProcessingData[];
}

export interface WorkflowStatusData {
  total_Workflow: number;
  overall_process_done: number;
  overall_process_fail: number;
  overall_process_inprogress: number;
}

export interface WorkflowStatusResponse {
  success: boolean;
  code: number;
  data: WorkflowStatusData;
}

export interface WorkflowExecutionDataPoint {
  createdAt: string;
  total_executions: number;
  total_workflow_done: number;
  total_workflow_failed: number;
}

export interface WorkflowExecutionResponse {
  success: boolean;
  code: number;
  data: {
    data: WorkflowExecutionDataPoint[];
  };
}

export interface WorkflowTemplateStatusData {
  total_workflow_template: number;
  total_active_workflow_template: number;
  total_inactive_workflow_template: number;
}

export interface WorkflowTemplateStatusResponse {
  success: boolean;
  code: number;
  data: WorkflowTemplateStatusData;
}

class DashboardService {
  async getMetrics(): Promise<DashboardMetricsResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.METRICS, {});
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  }

  async getProcessingVolume(startDate: string, endDate: string): Promise<DashboardProcessingVolumeResponse> {
    try {
      const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return `${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}-${date.getFullYear()}`;
      };

      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.PROCESSING_VOLUME, {
        start_time: formatDate(startDate),
        end_time: formatDate(endDate),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard processing volume:', error);
      throw error;
    }
  }

  async getWorkflowStatus(): Promise<WorkflowStatusResponse> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.WORKFLOW_STATUS);
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow status:', error);
      throw error;
    }
  }

  async getWorkflowExecution(startDate: string, endDate: string): Promise<WorkflowExecutionResponse> {
    try {
      const response = await apiClient.post(API_ENDPOINTS.DASHBOARD.WORKFLOW_EXECUTION, {
        start_date: startDate,
        end_date: endDate,
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow execution:', error);
      throw error;
    }
  }

  async getWorkflowTemplateStatus(): Promise<WorkflowTemplateStatusResponse> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.DASHBOARD.WORKFLOW_TEMPLATE_STATUS);
      return response.data;
    } catch (error) {
      console.error('Error fetching workflow template status:', error);
      throw error;
    }
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
